import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useDebtGoals,
  useCreateDebtGoal,
  useDeleteDebtGoal,
  useCompleteDebtGoal,
} from '../hooks/useDebt';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DebtForm from '../components/debt/DebtForm';

const FREE_DEBT_LIMIT = 1;

function fmt(value, currency = 'USD') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });
}

function DebtTypeIcon({ type }) {
  const icons = {
    credit_card: '💳',
    personal_loan: '🏦',
    student_loan: '🎓',
    car_loan: '🚗',
    mortgage: '🏠',
    medical: '🏥',
    other: '💸',
  };
  return <span>{icons[type] || '💸'}</span>;
}

function DebtCard({ goal, currency, onDelete, onComplete }) {
  const t = useT();
  const plan = goal.latest_plan;
  const statusColor =
    goal.status === 'completed'
      ? 'text-green-400'
      : goal.status === 'archived'
      ? 'text-slate-500'
      : 'text-brand-400';

  return (
    <div className="card hover:border-slate-600 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <DebtTypeIcon type={goal.debt_type} />
          <div>
            <h3 className="font-semibold text-slate-200">{goal.title}</h3>
            <p className="text-xs text-slate-500">{t(`debt.type.${goal.debt_type}`)}</p>
          </div>
        </div>
        <span className={`text-xs font-medium ${statusColor}`}>
          {t(`debt.status.${goal.status}`)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-xs text-slate-500">{t('debt.card.total_debt')}</p>
          <p className="text-lg font-bold text-slate-100">{fmt(goal.total_debt, currency)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">{t('debt.card.interest_rate')}</p>
          <p className="text-lg font-bold text-slate-100">{parseFloat(goal.interest_rate || 0).toFixed(2)}%/yr</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">{t('debt.card.min_payment')}</p>
          <p className="text-sm font-semibold text-slate-300">{fmt(goal.minimum_payment, currency)}/mo</p>
        </div>
        {plan?.status === 'active' && (
          <div>
            <p className="text-xs text-slate-500">{t('debt.card.payoff_plan')}</p>
            <p className="text-sm font-semibold text-slate-300">
              {plan.months_to_payoff} {t('debt.card.months')}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/debt/${goal.id}/plan`}
          className="btn-primary text-sm flex-1 text-center"
        >
          {plan?.status === 'active'
            ? t('debt.card.view_plan')
            : plan?.status === 'generating'
            ? t('debt.card.generating')
            : t('debt.card.get_plan')}
        </Link>
        {goal.status === 'active' && (
          <button
            onClick={() => onComplete(goal)}
            className="text-xs text-green-400 hover:text-green-300 transition-colors px-2 py-1 rounded border border-green-700/30 hover:border-green-600/50"
          >
            {t('debt.card.complete')}
          </button>
        )}
        <button
          onClick={() => onDelete(goal)}
          className="text-xs text-slate-500 hover:text-red-400 transition-colors px-2 py-1"
        >
          {t('common.delete')}
        </button>
      </div>
    </div>
  );
}

export default function DebtPage() {
  const t = useT();
  const { user, isPaid } = useAuthStore();
  const currency = user?.currency || 'USD';
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: goals = [], isLoading } = useDebtGoals();
  const { mutateAsync: createGoal, isPending: isCreating } = useCreateDebtGoal();
  const { mutateAsync: deleteGoal } = useDeleteDebtGoal();
  const { mutateAsync: completeGoal } = useCompleteDebtGoal();

  const activeGoals = goals.filter((g) => g.status === 'active');
  const completedGoals = goals.filter((g) => g.status === 'completed');
  const atLimit = !isPaid() && activeGoals.length >= FREE_DEBT_LIMIT;

  async function handleCreate(data) {
    await createGoal(data);
    setIsModalOpen(false);
  }

  async function handleDelete(goal) {
    if (!window.confirm(t('debt.confirm.delete').replace('{title}', goal.title))) return;
    await deleteGoal(goal.id);
  }

  async function handleComplete(goal) {
    if (!window.confirm(t('debt.confirm.complete').replace('{title}', goal.title))) return;
    await completeGoal(goal.id);
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{t('debt.title')}</h1>
          <p className="text-slate-400 mt-1">
            {isPaid()
              ? t('debt.subtitle.paid').replace('{n}', activeGoals.length)
              : t('debt.subtitle.free').replace('{n}', activeGoals.length).replace('{limit}', FREE_DEBT_LIMIT)}
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          disabled={atLimit}
          title={atLimit ? t('debt.limit.tooltip') : undefined}
        >
          {t('debt.new')}
        </Button>
      </div>

      {/* Free tier limit banner */}
      {atLimit && (
        <div className="rounded-xl border border-amber-700/50 bg-amber-950/30 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-amber-400 font-medium text-sm">{t('debt.limit.title')}</p>
            <p className="text-amber-300/70 text-xs mt-0.5">{t('debt.limit.sub')}</p>
          </div>
          <Link to="/pricing" className="flex-shrink-0 btn-primary text-sm">
            {t('debt.limit.cta')}
          </Link>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid md:grid-cols-2 gap-4 animate-pulse">
          {[1, 2].map((i) => (
            <div key={i} className="h-52 bg-surface-card rounded-2xl" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && goals.length === 0 && (
        <div className="card text-center py-16">
          <p className="text-5xl mb-4">💳</p>
          <p className="text-slate-300 font-semibold text-lg">{t('debt.empty.title')}</p>
          <p className="text-slate-500 text-sm mt-2 mb-8">{t('debt.empty.sub')}</p>
          <Button onClick={() => setIsModalOpen(true)}>{t('debt.empty.cta')}</Button>
        </div>
      )}

      {/* Active goals */}
      {!isLoading && activeGoals.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            {t('debt.section.active')}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {activeGoals.map((goal) => (
              <DebtCard
                key={goal.id}
                goal={goal}
                currency={currency}
                onDelete={handleDelete}
                onComplete={handleComplete}
              />
            ))}
          </div>
        </section>
      )}

      {/* Completed goals */}
      {!isLoading && completedGoals.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            {t('debt.section.completed')}
          </h2>
          <div className="grid md:grid-cols-2 gap-4 opacity-60">
            {completedGoals.map((goal) => (
              <DebtCard
                key={goal.id}
                goal={goal}
                currency={currency}
                onDelete={handleDelete}
                onComplete={handleComplete}
              />
            ))}
          </div>
        </section>
      )}

      {/* Create modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('debt.form.modal_title')}
      >
        <DebtForm onSubmit={handleCreate} isSubmitting={isCreating} />
      </Modal>
    </div>
  );
}
